import { Component, Input, OnInit } from '@angular/core';
import { ResourcePage, PurchasePageData } from 'src/app/shared/models/resource-page.models';
import { CompleteEnrollmentCallbackModel, OrderModel, PurchaseCallbackModel } from 'src/app/shared/models/order.models';
import { SessionStoreService } from 'src/app/core/services/stores/session-store.service';
import { PurchaseModalComponent } from 'src/app/portal/modals/purchase-modal/purchase-modal.component';
import { PurchaseApiService } from 'src/app/core/services/http/purchase-api.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationComponent } from 'src/app/portal/modals/confirmation/confirmation.component';
import { EnrollmentFormComponent } from 'src/app/portal/modals/enrollment-form/enrollment-form.component';
import { SurveyRenewalModel } from 'src/app/shared/models/surveyRenewal.models';
import { EnrollmentFormApiService } from 'src/app/core/services/http/enrollment-form-api.service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { RenewalFormComponent } from 'src/app/portal/modals/renewal-form/renewal-form.component';
import { MarketingStrategy } from 'src/app/shared/models/marketingStrategy.models';
import { SsoService } from 'src/app/core/services/sso.service';
import { fadeIn } from 'src/app/animations';

@Component({
  selector: 'app-retire-up-workflow',
  templateUrl: './retire-up-workflow.component.html',
  animations: [fadeIn],
  standalone: false
})
export class RetireUpWorkflowComponent implements OnInit {

  @Input() resource!: ResourcePage;
  purchased!: OrderModel | undefined;
  isLoading = false;
  intervals: any[] = [];
  @Input() submitted!: { surveyId: number, isEnrolled: boolean }[] | undefined;
  hasPastRenewals = false;
  surveyId: number | undefined;
  pendingRenewal = false;
  surveyRenewals!: SurveyRenewalModel[];


  constructor(
    private sessionStore: SessionStoreService,
    private purchaseApiService: PurchaseApiService,
    private dialog: MatDialog,
    private enrollmentFormApiService: EnrollmentFormApiService,
    private ssoService: SsoService
  ) { }

  ngOnInit(): void {
    this.isLoading = true;
    this.surveyId = this.resource.EnrollmentSurveyData?.SurveyIds[0];
    this.getAndRefreshSurveyRenewals().subscribe(() => {
      if (this.pendingRenewal) {
        this.openSurveyRenewalDialog();
      }
    });
    this.verifyPurchased();
  }

  getAndRefreshSurveyRenewals(): Observable<any> {
    return this.enrollmentFormApiService.getSurveyRenewals(this.surveyId).pipe(map(results => {
      this.surveyRenewals = results;
      const renewal = results.find(r => r.SurveyId === this.surveyId);
      if (renewal) {
        if (renewal.SurveyRenewalStatusId === 1) {
          this.pendingRenewal = true;
        } else {
          this.pendingRenewal = false;
          this.hasPastRenewals = true;
        }
      }
    }));
  }

  openSurveyRenewalDialog(): void {
    const ref = this.dialog.open(RenewalFormComponent, {
      data: {
        SurveyRenewals: this.surveyRenewals,
        FilterBySurveyId: this.surveyId
      }
    });

    ref.afterClosed().subscribe(result => {
      if (result) {
        this.verifyPurchased();
        this.getAndRefreshSurveyRenewals();
      }
    });
  }

  openEnrollmentModal(surveyId: number): void {
    const dialogRef = this.dialog.open(EnrollmentFormComponent, {
      data: {
        Event: this.resource.EnrollmentSurveyData?.Event,
        SurveyId: surveyId,
        Disclaimer: this.resource.EnrollmentSurveyData?.Disclaimer
      }
    });

    dialogRef.afterClosed().subscribe((res: CompleteEnrollmentCallbackModel) => {
      const survey = this.submitted?.find(item => item.surveyId === surveyId);
      if (survey && res.hasCompletedEnrollment) survey.isEnrolled = true;
    });
  }

  openDocusignLink(url: string): void {
    window.open(url);
  }

  verifyPurchased(): any {
    if (!this.hasPastRenewals) {
      return this.purchaseApiService.getUserOrders(this.sessionStore.User.Id).subscribe(orders => {
        this.hasPastRenewals = orders.some(o => {
          return o.MarketingStrategy === MarketingStrategy.RetireUp && o.ArcSurveyRenewalId !== null;
        });
        this.purchased = orders.find(o => {
          return o.MarketingStrategy === MarketingStrategy.RetireUp && o.ArcSurveyRenewalId === null;
        });

        if (this.purchased?.Id) {
          const d = new Date(this.purchased.CompletedDate as Date);
          const n = d.toLocaleDateString();
          this.purchased.PurchaseDate = n;

          this.purchaseApiService.getMarketingStrategyOfOrder(this.purchased.Id).subscribe(strategy => {
            if (strategy.DocusignUrl && strategy.DocusignUrl !== null && strategy.DocusignUrl !== '' && this.purchased) {
              this.purchased.DocusignUrl = strategy.DocusignUrl;
            }
            this.isLoading = false;
          });
          this.intervals?.forEach((interval: any) => {
            clearInterval(interval);
          });
        } else this.isLoading = false;
      });
    } else this.isLoading = false;
  }

  activateRetireUpSso(): void {
    // this.ssoService.retireUpSso();
    window.open('https://elite.retireup.com/login', '_blank');
  }

  pollVerifyPurchase(paymentAmount: number): void {
    if (paymentAmount > 0) {
      this.intervals.push(<any>setInterval(() => {
        this.verifyPurchased();
      }, 3000));
    } else {
      this.verifyPurchased();
    }
  }

  openPurchaseDialog(): void {
    const data = this.resource.PurchasePageData;
    if (data) {
      if (data?.ConfirmationMessage) {
        // Confirmation required
        const confirmation = this.dialog.open(ConfirmationComponent, {
          data: {
            title: data.PurchaseModel.Title,
            message: data.ConfirmationMessage
          }
        });

        confirmation.afterClosed().subscribe(result => {
          if (result) {
            // confirmation accepted
            this.purchaseResource(data);
          }
        });
      } else {
        // confirmation not required
        this.purchaseResource(data);
      }
    }
  }

  purchaseResource(data: PurchasePageData): void {

    this.purchaseApiService.getAllOrders().subscribe(orders => {

      data.PurchaseModel.Orders = orders;
      this.purchaseApiService.getMarketingDollars().subscribe(agentMarketingDollars => {
        data.PurchaseModel.AgentMarketingDollars = agentMarketingDollars.Entry.MarketingDollarsTotal;
      }, () => {
        data.PurchaseModel.AgentMarketingDollars = 0;
      }, () => {
        // finally block
        // open the modal finally
        const ref = this.dialog.open(PurchaseModalComponent, {
          data: {
            purchaseModel: data.PurchaseModel,
          }
        });

        ref.afterClosed().subscribe((result: PurchaseCallbackModel) => {
          if (result) {
            this.pollVerifyPurchase(result.paymentAmount);
          }
        });
      });
    });
  }
}

